import {
    AddIcon,
    Button,
    ButtonIcon,
    ButtonText,
    Center,
    ChevronLeftIcon,
    HStack,
    Text,
    useChannel,
    useListing,
    useListingService,
    VStack
} from "@hosttools/frontend";
import {type AccountType} from "@hosttools/frontend/models/account";
import {type FC, memo, useCallback, useEffect, useState} from "react";
import {useHistory, useParams, useRouteMatch} from "react-router-dom";

import TableAccountListings from "../AddListing/ImportListings/TableAccountListings";

import ModalAddAccount from "@/admin/components/ModalAddAccount";
import useSearchQuery from "@/admin/hooks/useSearchQuery";
import ContentWrapper from "@/client/components/ContentWrapper";
import Heading from "@/client/components/Heading";
import Paragraph from "@/client/components/Paragraph";
import useAccountsByType from "@/client/hooks/useAccountsByType";

type Params = {
    listingID: string;
    accountType: AccountType;
};

// parent route elemnt shouldn't take more params
const ConnectListing: FC = () => {
    const {listingID, accountType} = useParams<Params>();
    const search = useSearchQuery();
    const {url} = useRouteMatch();
    const [addAccountType, setAddAccountType] = useState<AccountType>();
    const listingService = useListingService();
    const {accounts} = useAccountsByType(accountType);
    const history = useHistory();

    const goBack = useCallback(() => {
        const returnUrl = search.get("returnUrl");
        if (returnUrl) {
            history.push(decodeURIComponent(returnUrl));
        }
    }, [search, history]);

    const handleConnect = useCallback(
        async (accountID: string, externalListingID: string) => {
            await listingService.connectListingToAccount(accountID, listingID, externalListingID);
            goBack();
        },
        [listingID, listingService, goBack]
    );

    const hideAddAccountModal = useCallback(() => {
        setAddAccountType(undefined);
    }, []);

    const handleAddAdditionalAccount = useCallback(() => {
        setAddAccountType(accountType);
    }, [accountType]);

    const {label} = useChannel(accountType);
    const listing = useListing(listingID);

    // auto open the connect account modal in case of no connected accounts
    useEffect(() => {
        if (accounts.length < 1) {
            handleAddAdditionalAccount();
        }
    }, [accounts.length, handleAddAdditionalAccount]);

    return (
        <ContentWrapper fitHeight>
            <VStack className="p-4">
                <VStack space="md">
                    <Heading variant="2xl">{`${label} listings`}</Heading>
                    <Paragraph variant="lg">
                        Select the matching listing for <Text variant="lg">{listing?.name}</Text> on{" "}
                        <Text variant="lg">{label}</Text>
                    </Paragraph>
                </VStack>
                <VStack space="lg">
                    {accounts.map(elem => (
                        <TableAccountListings
                            key={elem._id}
                            accountID={elem._id}
                            type="matchListing"
                            onConnect={handleConnect}
                        />
                    ))}

                    {accounts.length > 0 && (
                        <Button
                            className="mt-4"
                            variant="outline"
                            onPress={handleAddAdditionalAccount}
                        >
                            <ButtonIcon as={AddIcon} />
                            <ButtonText>Add Account</ButtonText>
                        </Button>
                    )}
                </VStack>
            </VStack>

            {!accounts.length && (
                <Center className="flex-1">
                    <Text variant="xl">{`Let's connect your first ${label} account`}</Text>
                    <Button onPress={handleAddAdditionalAccount}>
                        <ButtonIcon as={AddIcon} />
                        <ButtonText>Connect account</ButtonText>
                    </Button>
                </Center>
            )}

            <HStack className="p-4 self-end">
                <Button variant="outline" onPress={goBack}>
                    <ButtonIcon as={ChevronLeftIcon} />
                    <ButtonText>Back</ButtonText>
                </Button>
            </HStack>

            {addAccountType && (
                <ModalAddAccount
                    channel={addAccountType}
                    oAuthReturnUrl={
                        // consider `/add-listing` is the main landing page
                        typeof ONBOARDING_OAUTH_RETURN_URL !== "undefined"
                            ? ONBOARDING_OAUTH_RETURN_URL
                            : `${window.location.origin}/add-listing`
                    }
                    oAuthStateParameter={url}
                    onConnected={hideAddAccountModal}
                    onHide={hideAddAccountModal}
                />
            )}
        </ContentWrapper>
    );
};

export default memo(ConnectListing);
